import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  mdiDelete,
  mdiEyeOutline,
  mdiFile,
  mdiFileImage,
  mdiFilePdfBox,
  mdiPencilOutline
} from "@mdi/js";
import Icon from "@mdi/react";

import Select from "components/Select";
import MultiSelect from "components/MultiSelect";
import { UPLOAD_CATEGORY } from "constants/base";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IOption } from "models/Form";
import { IFRAttachment } from "models/HaulPay";
import { deleteFRAttachmentAPI, updateAttachment } from "services/api/haulpay";
import { useModal } from "context/ModalContext";
import { useUserFeature } from "hooks/useUserFeature";

const getFileIcon = (filename: string = "") => {
  const extension = filename.split(".").pop()?.toLowerCase();

  if (extension === "pdf") {
    return mdiFilePdfBox;
  }

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
    return mdiFileImage;
  }

  return mdiFile;
};

const normalizeCategoryValue = (val: string): string => {
  const raw = String(val ?? "").trim();
  if (!raw) return "";
  const match = UPLOAD_CATEGORY.find(
    (opt) => String(opt.value).toLowerCase() === raw.toLowerCase()
  );
  return (match?.value as string) || raw;
};

const stringifyCategory = (category: IFRAttachment["category"]): string => {
  if (Array.isArray(category)) return category.map(normalizeCategoryValue).join(",");
  return normalizeCategoryValue(String(category ?? ""));
};

const visibilityOptionFromAttachment = (attachment: IFRAttachment): IOption => {
  const visibilityArray = [...(attachment.visibleTo ?? attachment.visible_to ?? [])];

  const hasDebtor = visibilityArray.includes("debtor");
  const hasShipper = visibilityArray.includes("shipper");

  if (hasDebtor && !hasShipper) {
    visibilityArray.push("shipper");
  } else if (hasShipper && !hasDebtor) {
    visibilityArray.push("debtor");
  }

  const match = VISIBILITY_LIST.find((opt) => {
    if (!opt.value) return false;
    const optionValues = opt.value.split(",");
    return (
      optionValues.length === visibilityArray.length &&
      optionValues.every((val) => visibilityArray.includes(val))
    );
  });

  return match ?? VISIBILITY_LIST[0];
};

const categoryOptionFromAttachment = (attachment: IFRAttachment): IOption[] | undefined => {
  const rawCategory = stringifyCategory(attachment.category);
  if (!rawCategory) return undefined;

  const categoryValues = rawCategory
    .split(",")
    .map((cat) => cat.trim())
    .filter(Boolean);
  if (!categoryValues.length) return undefined;

  const options = categoryValues
    .map((categoryValue) =>
      UPLOAD_CATEGORY.find((opt) => String(opt.value).toLowerCase() === categoryValue.toLowerCase())
    )
    .filter(Boolean) as IOption[];

  return options.length ? options : undefined;
};

interface AttachmentRowProps {
  attachment: IFRAttachment;
  disabled: boolean;
  isDeleting: boolean;
  fundingRequestId?: string;
  onDelete: (id: string) => void;
  onUpdate: (id: string, patch: Partial<IFRAttachment>) => void;
  onEdit?: (attachment: IFRAttachment) => void;
}

function AttachmentRow({
  attachment,
  disabled,
  isDeleting,
  fundingRequestId,
  onDelete,
  onUpdate,
  onEdit
}: Readonly<AttachmentRowProps>) {
  const { isCarrier } = useUserFeature();
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [visibilityOption, setVisibilityOption] = useState<IOption>(() =>
    visibilityOptionFromAttachment(attachment)
  );
  const [categoryOption, setCategoryOption] = useState<IOption[] | undefined>(() =>
    categoryOptionFromAttachment(attachment)
  );

  useEffect(() => {
    setVisibilityOption(visibilityOptionFromAttachment(attachment));
    setCategoryOption(categoryOptionFromAttachment(attachment));
  }, [attachment]);

  const previewUrl = attachment.preview_url ?? attachment.previewUrl ?? attachment.downloadUrl;

  const handlePreview = useCallback(() => {
    if (!previewUrl) return;
    const newWindow = window.open(previewUrl, "_blank");
    if (!newWindow) {
      showErrorMessage("Please allow popups to preview this attachment.");
    } else {
      newWindow.focus();
    }
  }, [previewUrl]);

  const handleVisibilityChange = useCallback(
    async (option: IOption) => {
      const previous = visibilityOption;
      setVisibilityOption(option);

      const visibleList = option.value ? option.value.split(",") : [];

      if (!fundingRequestId) {
        onUpdate(attachment.id, { visibleTo: visibleList, visible_to: visibleList });
        return;
      }

      setUpdatingVisibility(true);
      try {
        await updateAttachment<IFRAttachment>({
          attachment: {
            ...attachment,
            category: stringifyCategory(attachment.category),
            visible_to: visibleList
          },
          fundingRequestId,
          attachmentId: attachment.id
        });
        onUpdate(attachment.id, { visibleTo: visibleList, visible_to: visibleList });
        showSuccessMessage("Visibility updated.");
      } catch (error) {
        setVisibilityOption(previous);
        showErrorMessage("Failed to update visibility.");
      } finally {
        setUpdatingVisibility(false);
      }
    },
    [attachment, fundingRequestId, onUpdate, visibilityOption]
  );

  const handleLocalCategoryChange = useCallback((options: IOption[]) => {
    setCategoryOption(options);
  }, []);

  const handleCategoryChange = useCallback(
    async (options: IOption[]) => {
      const categoryValues = options.map((opt) => opt.value).join(",");
      const previousCategory = attachment.category || "";

      if (!fundingRequestId) {
        onUpdate(attachment.id, { category: categoryValues });
        setIsEditingCategory(false);
        return;
      }

      const visibleList = visibilityOption.value ? visibilityOption.value.split(",") : [];
      const body: IFRAttachment = {
        ...attachment,
        category: categoryValues,
        visible_to: visibleList
      };
      if (isCarrier) delete body.visible_to;
      setUpdatingCategory(true);
      // Optimistic update
      onUpdate(attachment.id, { category: categoryValues });
      try {
        await updateAttachment<IFRAttachment>({
          attachment: body,
          fundingRequestId,
          attachmentId: attachment.id
        });
        showSuccessMessage("Category updated.");
        setIsEditingCategory(false);
      } catch (error) {
        // Rollback on failure
        onUpdate(attachment.id, { category: previousCategory });
        showErrorMessage("Failed to update category.");
      } finally {
        setUpdatingCategory(false);
      }
    },
    [attachment, fundingRequestId, isCarrier, onUpdate, visibilityOption]
  );

  const handleEditClick = useCallback(() => {
    const filename = attachment.filename || "";
    const extension = filename.split(".").pop()?.toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");
    if (isImage && onEdit) {
      onEdit(attachment);
    } else {
      setIsEditingCategory((prev) => !prev);
    }
  }, [attachment, onEdit]);

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-card bg-card p-4
    shadow-[0_6px_12px_rgba(0,0,0,0.12)]
    transform translate-y-[-2px]
    transition-all duration-200 w-full">
      <div className="flex items-start w-full">
        <div className="flex items-start gap-2 w-full min-w-0">
          <div className="w-6 shrink-0">
            <Icon
              path={getFileIcon(attachment.filename)}
              className="flex-1 text-primary !h-6 !w-6"
            />
          </div>
          <div className="text-sm flex-1 min-w-0">
            <div className="truncate font-medium min-w-0">
              {attachment.filename || "Unnamed file"}
            </div>

            {!isEditingCategory && (
              <div className="truncate text-xs text-placeholder mt-1 min-w-0">
                {Array.isArray(attachment.category)
                  ? attachment.category.join(", ")
                  : stringifyCategory(attachment.category) || "No category"}
              </div>
            )}

            {isEditingCategory && (
              <div className="mt-2 w-full">
                <MultiSelect
                  options={UPLOAD_CATEGORY}
                  value={categoryOption || []}
                  onChange={handleLocalCategoryChange}
                  disabled={disabled || updatingCategory}
                  className="text-xs"
                  btnClass="!py-1 !px-2 !text-xs"
                  onDropdownClose={handleCategoryChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-card/50 pt-2 mt-1">
        {!isCarrier && (
          <Select
            options={VISIBILITY_LIST}
            value={visibilityOption}
            onChange={(opt) => opt && handleVisibilityChange(opt)}
            disabled={disabled || updatingVisibility}
            className="text-xs"
            btnClass="!py-1 !px-2 !text-xs !bg-transparent !border-card"
          />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePreview}
            className="text-primary hover:text-primary-300 disabled:opacity-50"
            disabled={!previewUrl}
            title="View file">
            <Icon path={mdiEyeOutline} size={0.8} />
          </button>

          <button
            type="button"
            onClick={handleEditClick}
            className="text-primary hover:text-primary-300 disabled:opacity-50"
            disabled={disabled}
            title={isEditingCategory ? "Close" : "Edit"}>
            <Icon path={mdiPencilOutline} size={0.8} />
          </button>

          {!disabled && (
            <button
              type="button"
              onClick={() => onDelete(attachment.id)}
              disabled={isDeleting}
              className="rounded-full p-1 text-error transition-colors hover:bg-error/10 disabled:opacity-50"
              title="Delete attachment">
              <Icon path={mdiDelete} size={0.8} className={isDeleting ? "animate-pulse" : ""} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface AttachmentsListProps {
  attachments: IFRAttachment[];
  onAttachmentsChange: (attachments: IFRAttachment[]) => void;
  fundingRequestId?: string;
  onEditAttachment?: (attachment: IFRAttachment) => void;
}

export default function AttachmentsList({
  attachments,
  onAttachmentsChange,
  fundingRequestId,
  onEditAttachment
}: Readonly<AttachmentsListProps>) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { openModal, closeModal } = useModal();
  const hasAttachments = useMemo(
    () => Array.isArray(attachments) && attachments.length > 0,
    [attachments]
  );

  const handleAttachmentUpdate = useCallback(
    (id: string, patch: Partial<IFRAttachment>) => {
      const updated = attachments.map((attachment) =>
        attachment.id === id
          ? {
              ...attachment,
              ...patch,
              visibleTo: patch.visibleTo ?? attachment.visibleTo,
              visible_to: patch.visible_to ?? attachment.visible_to
            }
          : attachment
      );
      onAttachmentsChange(updated as IFRAttachment[]);
    },
    [attachments, onAttachmentsChange]
  );

  const handleDeleteAttachment = useCallback(
    async (attachmentId: string) => {
      const previousAttachments = [...attachments];
      try {
        setDeletingId(attachmentId);
        if (fundingRequestId) {
          await deleteFRAttachmentAPI(fundingRequestId, attachmentId);
        }
        const updatedAttachments = attachments.filter((att) => att.id !== attachmentId);
        onAttachmentsChange(updatedAttachments as IFRAttachment[]);
        showSuccessMessage("Attachment removed successfully");
      } catch (error) {
        onAttachmentsChange(previousAttachments);
        showErrorMessage("Failed to remove attachment");
      } finally {
        setDeletingId(null);
      }
    },
    [attachments, onAttachmentsChange, fundingRequestId]
  );

  const handleConfirmDeleteAttachment = useCallback(
    (id: string) =>
      openModal("warning", {
        message: "Are you sure you want to delete this attachment?",
        onConfirm: () => {
          handleDeleteAttachment(id);
          closeModal("warning");
        },
        onClose: () => closeModal("warning")
      }),
    [handleDeleteAttachment, openModal, closeModal]
  );

  if (!hasAttachments) {
    return null;
  }

  return (
    <div className="space-y-2 mt-4">
      {attachments.map((attachment) => (
        <AttachmentRow
          key={attachment.id}
          attachment={attachment}
          disabled={false}
          fundingRequestId={fundingRequestId}
          isDeleting={deletingId === attachment.id}
          onDelete={handleConfirmDeleteAttachment}
          onUpdate={handleAttachmentUpdate}
          onEdit={onEditAttachment}
        />
      ))}
    </div>
  );
}
